import {displayApi,invalidateOperatorSummary,type FollowerDisplay} from '@/lib/forex-display-api';
import {AccountSnapshotPanel,ConnectionCell} from './AccountDisplayCells';
import ManagedInstancePanel from './ManagedInstancePanel';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { configAgentError, forexApi, type ProvisioningJob, type TestAccountAccess } from "@/lib/forex-api";

type AuditAction = "approve" | "reject" | "request_material";

const statusChip: Record<string, { cls: string; label: string; icon: string }> = {
  reviewing: { cls: "bg-amber-500/10 text-amber-500", label: "待人工审核", icon: "ri-time-fill" },
  ea_provisioning: { cls: "bg-brand/10 text-brand", label: "审核通过", icon: "ri-loader-4-line" },
  connected: { cls: "bg-emerald-500/10 text-emerald-500", label: "审核通过", icon: "ri-checkbox-circle-fill" },
  rejected: { cls: "bg-red-500/10 text-red-500", label: "已拒绝", icon: "ri-close-circle-fill" },
  need_material: { cls: "bg-amber-500/10 text-amber-500", label: "要求补充", icon: "ri-file-add-line" },
};

function formatTime(timestamp?: number) {
  if (!timestamp) return "--";
  return new Date(timestamp * 1000).toLocaleString("sv-SE").slice(0, 19);
}

function Chip({ value, map }: { value: string; map: Record<string, { cls: string; label: string; icon: string }> }) {
  const config = map[value] || { cls: "bg-surface text-text-muted", label: value || "--", icon: "ri-question-line" };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap ${config.cls}`}>
      <i className={`${config.icon} text-[10px]`} />{config.label}
    </span>
  );
}

export default function SubAuditDetailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawId = searchParams.get("id") || "";
  const [review, setReview] = useState<FollowerDisplay | null>(null);
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);
  const selectedId = useRef(rawId); selectedId.current = rawId;
  const [error, setError] = useState("");
  const [reason, setReason] = useState("");
  const [activeAction, setActiveAction] = useState<AuditAction | null>(null);
  const operationKeys = useRef<Record<string, string>>({});
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);
  const [localLogs, setLocalLogs] = useState<Array<{ time: string; action: string; detail: string }>>([]);
  const [jobs, setJobs] = useState<ProvisioningJob[]>([]);
  const [jobsError, setJobsError] = useState("");
  const [jobsLoading, setJobsLoading] = useState(false);
  const jobsEpoch = useRef(0);
  const invalidateJobs = useCallback(() => { jobsEpoch.current++; }, []);
  const loadJobs = useCallback(async (accountId: string) => {
    const epoch = ++jobsEpoch.current;
    setJobsLoading(true); setJobsError("");
    try { const result = await forexApi.listProvisioningJobs({ target_type: "follower_account", target_id: accountId }); if(epoch === jobsEpoch.current) setJobs(result.items); }
    catch { if(epoch === jobsEpoch.current) setJobsError("开通任务暂不可确认，请重新读取。"); }
    finally { if(epoch === jobsEpoch.current) setJobsLoading(false); }
  }, []);
  const [testAccess, setTestAccess] = useState<TestAccountAccess | null>(null);
  const [canManageTestAccess, setCanManageTestAccess] = useState(false);
  const [testAccessLoading, setTestAccessLoading] = useState(false);
  const [testAccessError, setTestAccessError] = useState("");
  const [testAccessSaving, setTestAccessSaving] = useState(false);
  const testAccessRequest = useRef<{ enabled: boolean; key: string } | null>(null);

  const showToast = (type: "success" | "error", msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 2800);
  };

  useEffect(() => {
    let cancelled = false;
    jobsEpoch.current++; setJobs([]); setJobsError(""); setReview(null);
    setLoading(true);
    setError("");
    displayApi.follower(rawId)
      .then((result) => {
        if (cancelled) return;
        const matched = result;
        setReview(matched);
        if (!matched) setError("未找到子账号审核申请，请从子账号审核列表重新进入。");
        if (matched) {
          void loadJobs(matched.follower_account_id);
        }
      })
      .catch(() => {
        if (!cancelled) setError("子账号审核详情加载失败，请检查后台服务或登录态。");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true; invalidateJobs();
    };
  }, [rawId, revision, loadJobs, invalidateJobs]);

  useEffect(() => {
    if (!review?.follower_account_id) return;
    let cancelled = false;
    setTestAccessLoading(true);
    setTestAccessError("");
    forexApi.getTestAccountAccess(review.follower_account_id)
      .then((result) => { if (!cancelled) {
        setTestAccess(result.item); setCanManageTestAccess(result.can_manage);
      } })
      .catch(() => { if (!cancelled) {
        setCanManageTestAccess(false);
        setTestAccessError("测试资格暂不可确认，请刷新后重试");
      } })
      .finally(() => { if (!cancelled) setTestAccessLoading(false); });
    return () => { cancelled = true; };
  }, [review?.follower_account_id]);

  const updateTestAccess = async (enabled: boolean) => {
    if (!review || testAccessSaving || testAccessLoading || testAccessError) return;
    if (!testAccessRequest.current || testAccessRequest.current.enabled !== enabled) {
      testAccessRequest.current = { enabled, key: crypto.randomUUID() };
    }
    setTestAccessSaving(true);
    try {
      const result = await forexApi.putTestAccountAccess(review.follower_account_id,
        enabled, testAccess?.revision ?? 0, testAccessRequest.current.key);
      setTestAccess({ follower_account_id: review.follower_account_id,
        owner_scope: result.owner_scope, enabled: result.enabled, revision: result.revision,
        identity_valid: enabled, updated_at: result.updated_at, updated_by: "当前管理员" });
      testAccessRequest.current = null;
      showToast("success", enabled ? "已加入测试白名单" : "已移除测试白名单");
    } catch (error) {
      showToast("error", configAgentError(error, "测试资格操作失败"));
    } finally {
      setTestAccessSaving(false);
    }
  };

  const handleAction = async (decision: AuditAction) => {
    if (!review) return;
    if (decision !== "approve" && !reason.trim()) {
      showToast("error", "请先填写审核原因");
      return;
    }
    if (!review.submission_revision || !review.review_revision) {
      showToast("error", "审核资料版本缺失，请刷新后重试。");
      return;
    }
    const slot = `${review.review_id}:${decision}:${review.submission_revision}:${review.review_revision}:${decision === "approve" ? "" : reason.trim()}`;
    const operationKey = operationKeys.current[slot] ||= crypto.randomUUID();

    setActiveAction(decision);
    try {
      const logAction = decision === "approve" ? "审核通过" : decision === "reject" ? "审核拒绝" : "要求补充材料";
      const updated = await forexApi.patchFollowerAccountReview(review.review_id, {
        decision,
        reason: decision === "approve" ? "人工审核通过" : reason.trim(),
        expected_submission_revision: review.submission_revision,
        expected_review_revision: review.review_revision,
      }, operationKey);
      if (selectedId.current !== rawId) return;
      const logDetail = decision === "approve" ? (updated.terminal_status === "creating" ? "正在自动开通 EA" : "等待自动开通 EA") : reason.trim();
      delete operationKeys.current[slot];
      invalidateOperatorSummary();
      showToast("success", "审核请求已受理，正在核对最新状态");
      try { setReview(await displayApi.follower(rawId)); }
      catch { setError("审核已受理，详情回读暂不可用，请刷新核对。"); }
      await loadJobs(review.follower_account_id);
      setLocalLogs((prev) => [
        {
          time: formatTime(Math.floor(Date.now() / 1000)),
          action: logAction,
          detail: logDetail,
        },
        ...prev,
      ]);
      setReason("");
      showToast("success", decision === "approve" ? `审核通过，${logDetail}` : decision === "reject" ? "已拒绝该子账号申请" : "已要求用户补充材料");
    } catch {
      showToast("error", "提交结果待核对，请刷新原审核与开通任务；再次提交会复用原请求。");
    } finally {
      setActiveAction(null);
    }
  };

  const baseLogs = useMemo(() => {
    if (!review) return [];
    return [
      { time: formatTime(review.submitted_at), action: "App 提交审核", detail: `${review.user_ref} 提交 ${review.broker} ${review.mt4_login_masked}` },
      { time: formatTime(review.updated_at), action: "当前状态", detail: review.reason || `状态：${review.account_status}，Terminal：${review.terminal_status}` },
    ];
  }, [review]);

  return (
    <div className="space-y-5">
      {review?.instance && <ManagedInstancePanel key={review.instance.job_id} jobId={review.instance.job_id}/>}
      <Breadcrumb items={[{ label: "管理后台", href: "/forex/overview" }, { label: "子账号接入", href: "/forex/sub-accounts" }, { label: "接入详情" }]} />

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">子账号接入详情</h2>
          <p className="text-xs text-text-muted mt-0.5">{review ? `${review.review_id} · ${review.follower_account_id} · ${review.user_ref}` : rawId || "未选择申请"}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button disabled={loading || !!activeAction} onClick={() => setRevision(value => value + 1)} className="rounded-lg bg-surface px-3 py-1.5 text-xs text-brand">刷新详情</button>
          <button onClick={() => navigate("/forex/sub-accounts")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand cursor-pointer whitespace-nowrap">
            <i className="ri-arrow-left-line text-sm" />返回列表
          </button>
          {review?.account_status === "reviewing" && (
            <>
              <button onClick={() => handleAction("approve")} disabled={!!activeAction||!!error||loading} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 disabled:opacity-60 cursor-pointer whitespace-nowrap">
                {activeAction === "approve" ? <><i className="ri-loader-4-line animate-spin text-sm" />处理中</> : <><i className="ri-checkbox-circle-line text-sm" />审核通过</>}
              </button>
              <button onClick={() => handleAction("reject")} disabled={!!activeAction||!!error||loading} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/10 text-red-500 hover:bg-red-500/20 disabled:opacity-60 cursor-pointer whitespace-nowrap">
                {activeAction === "reject" ? <><i className="ri-loader-4-line animate-spin text-sm" />处理中</> : <><i className="ri-close-line text-sm" />拒绝</>}
              </button>
              <button onClick={() => handleAction("request_material")} disabled={!!activeAction||!!error||loading} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 disabled:opacity-60 cursor-pointer whitespace-nowrap">
                {activeAction === "request_material" ? <><i className="ri-loader-4-line animate-spin text-sm" />处理中</> : <><i className="ri-file-add-line text-sm" />补充材料</>}
              </button>
            </>
          )}
        </div>
      </div>

      {loading && <div className="bg-white rounded-lg border border-surface-border px-4 py-10 text-center text-sm text-text-muted">加载子账号审核详情...</div>}
      {!loading && error && <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-500">{error}</div>}

      {!loading && review && (
        <>
          <AccountSnapshotPanel facts={review}/>
          <div className="bg-white rounded-lg border border-surface-border px-4 py-3">
            <p className="text-xs font-semibold text-text-primary">账号支持品种</p>
            <p className="text-xs text-text-secondary mt-1">
              {review.symbol_support?.status === 'ready'
                ? `已验证 ${review.symbol_support.supported_symbols.length} 个：${review.symbol_support.supported_symbols.join('、')}`
                : '品种能力待验证，请核对 EA 连接及配置同步状态'}
            </p>
            {review.symbol_support?.observed_at && <p className="text-[10px] text-text-muted mt-1">最近验证：{formatTime(review.symbol_support.observed_at)}</p>}
            {review.symbol_support?.unresolved_symbols?.length ? <p className="text-xs text-amber-600 mt-1">待确认：{review.symbol_support.unresolved_symbols.join('、')}</p> : null}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">审核状态</p><div className="mt-1 flex justify-center"><Chip value={review.account_status} map={statusChip} /></div></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">开通状态</p><p className="text-xs font-mono font-semibold text-text-primary mt-1">{({approved:'已开通',failed:'开通失败',canceled:'已取消',unknown:'暂无开通记录'} as Record<string,string>)[review.provisioning.status]||'开通中'}</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">EA / Terminal</p><div className="mt-1 flex justify-center"><ConnectionCell facts={review}/></div></div>
            <div className="bg-white rounded-lg border border-surface-border px-3 py-2.5 text-center"><p className="text-[10px] text-text-muted">更新时间</p><p className="text-xs font-mono text-text-primary mt-1">{formatTime(review.updated_at)}</p></div>
          </div>

          <div className="bg-white rounded-lg border border-surface-border p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-text-primary">审核前验证资格</h3>
                <p className="text-xs text-text-muted mt-1">仅作用于交易账号 {review.follower_account_id}；账号所有者仍需确认跟随。</p>
                <p className="text-xs text-text-secondary mt-1" role="status">
                  {testAccessLoading ? "正在查询资格…" : testAccessError ||
                    (testAccess?.enabled ? (testAccess.identity_valid ? "已加入测试白名单" : "资格已失效，账号身份需核对") : "未加入测试白名单")}
                </p>
              </div>
              {canManageTestAccess && <button type="button" disabled={testAccessSaving || testAccessLoading || !!testAccessError ||
                (testAccess?.enabled ? false : review.account_status !== "connected")}
                onClick={() => updateTestAccess(!testAccess?.enabled)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-brand/10 text-brand disabled:opacity-50">
                {testAccessSaving ? "处理中…" : testAccess?.enabled ? "移除测试资格" : "加入测试白名单"}
              </button>}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white rounded-lg border border-surface-border p-4">
              <h3 className="text-sm font-semibold text-text-primary mb-3">申请信息</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { label: "审核 ID", value: review.review_id, mono: true },
                  { label: "子账号 ID", value: review.follower_account_id, mono: true },
                  { label: "App 用户", value: review.user_ref, mono: true },
                  { label: "经纪商", value: review.broker },
                  { label: "服务器", value: review.server, mono: true },
                  { label: "MT4 账号", value: review.mt4_login_masked, mono: true },
                  { label: "提交时间", value: formatTime(review.submitted_at), mono: true },
                  { label: "审核原因", value: review.reason || "--" },
                  { label: "流程边界", value: "App 提交 -> 自动准备 EA/Terminal -> EA 在线 -> App 可跟单" },
                ].map((row) => (
                  <div key={row.label} className="bg-surface/50 rounded-md p-2.5">
                    <span className="text-[10px] text-text-muted font-medium uppercase">{row.label}</span>
                    <p className={`text-xs text-text-primary mt-0.5 ${row.mono ? "font-mono" : ""}`}>{row.value}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg border border-surface-border p-4">
              <h3 className="text-sm font-semibold text-text-primary mb-3">操作原因</h3>
              <textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder="拒绝或要求补充材料时必填" className="w-full h-28 rounded-lg border border-surface-border bg-surface px-3 py-2 text-xs text-text-primary placeholder:text-text-muted focus:border-brand/50 focus:outline-none resize-none" />
              <p className="mt-2 text-[11px] text-text-muted">审核通过后服务端自动创建 EA 开通任务；若任务暂时未创建，系统会继续补偿。</p>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-surface-border p-4">
            <h3 className="text-sm font-semibold text-text-primary mb-3">EA 开通任务</h3>
            <div className="space-y-2">
              {jobsLoading && <p role="status">正在读取开通任务…</p>}
              {jobsError && <p role="alert" className="text-sm text-amber-700">{jobsError}<button onClick={() => review && void loadJobs(review.follower_account_id)} className="ml-2 underline">重新读取任务</button></p>}
              {jobs.map((job) => (
                <div key={job.provision_job_id} className="rounded-lg bg-surface/50 p-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <span className="text-xs font-mono text-text-primary">{job.provision_job_id}</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-brand/10 text-brand">{job.status}</span>
                  </div>
                  <p className="mt-2 text-xs">{job.automation_stage || job.status} · {job.last_error_code || "暂无失败原因"} · 已尝试 {job.attempt ?? "—"} 次</p>
                  <Link className="text-xs text-brand underline" to={`/forex/terminal/detail?job_id=${encodeURIComponent(job.provision_job_id)}`}>查看开通诊断与记录</Link>
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                    <span className="text-text-muted">role <b className="text-text-primary font-mono">{job.account_role}</b></span>
                    <span className="text-text-muted">terminal <b className="text-text-primary font-mono">{job.terminal_id}</b></span>
                    <span className="text-text-muted">目标 poll <b className="text-text-primary font-mono">{String(job.enable_command_poll)}</b></span>
                    <span className="text-text-muted">目标 order <b className="text-text-primary font-mono">{String(job.enable_live_order_send)}</b></span>
                  </div>
                </div>
              ))}
              {!jobsLoading && !jobsError && jobs.length === 0 && <p className="text-xs text-text-muted py-2 text-center">暂无开通任务</p>}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-surface-border p-4">
            <h3 className="text-sm font-semibold text-text-primary mb-3">提交与当前状态摘要</h3><p className="mb-2 text-xs text-text-muted">本页操作提示不是完整审计；持久处理记录请查看上方开通诊断与记录。</p>
            <div className="space-y-2">
              {[...localLogs, ...baseLogs].map((log, index) => (
                <div key={`${log.time}-${index}`} className="flex items-start gap-3 rounded-lg bg-surface/50 p-3">
                  <span className="mt-1.5 h-2 w-2 rounded-full bg-brand shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap"><span className="text-xs font-medium text-text-primary">{log.action}</span><span className="text-[10px] text-text-muted font-mono">{log.time}</span></div>
                    <p className="mt-0.5 text-[11px] text-text-secondary">{log.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Link to="/forex/terminal" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand whitespace-nowrap"><i className="ri-terminal-box-line text-sm" />Terminal / EA</Link>
            <Link to="/forex/follow" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand whitespace-nowrap"><i className="ri-user-follow-line text-sm" />跟单关系</Link>
          </div>
        </>
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${toast.type === "success" ? "bg-emerald-500 text-white" : "bg-red-500 text-white"}`}>
            <i className={`${toast.type === "success" ? "ri-checkbox-circle-fill" : "ri-error-warning-fill"} text-base`} />{toast.msg}
          </div>
        </div>
      )}
    </div>
  );
}
